import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchMetricStats,
  fetchMetricsHistory,
  type MetricDataPoint,
  type MetricsRange,
} from '@/lib/dashboard.ts';

/* From midnight UTC back, so the window means the same thing all day. */
function windowFor(days: number): { dateFrom: string; dateTo: string } {
  const to = new Date();
  to.setUTCHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);

  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
}

/**
 * The daily snapshots and the period's stats for the last `days` days, in one
 * organisation or all of them. `points` answers one metric's series.
 */
export function useMetricsHistory(days: number, organisationId: string | undefined) {
  const metricsRange = useMemo<MetricsRange>(
    () => ({
      ...windowFor(days),
      organisationIds: organisationId ? [organisationId] : [],
    }),
    [days, organisationId],
  );

  const history = useQuery({
    queryKey: ['metricsHistory', metricsRange],
    queryFn: () => fetchMetricsHistory(metricsRange),
    throwOnError: true,
  });

  const stats = useQuery({
    queryKey: ['metricStats', metricsRange],
    queryFn: () => fetchMetricStats(metricsRange),
    throwOnError: true,
  });

  const byMetric = useMemo(() => {
    const grouped: Record<string, MetricDataPoint[]> = {};
    for (const point of history.data?.data ?? []) {
      (grouped[point.metric] ??= []).push(point);
    }
    return grouped;
  }, [history.data]);

  const points = (metric: string): MetricDataPoint[] => byMetric[metric] ?? [];

  return {
    stats: stats.data,
    points,
    loading: history.isPending || stats.isPending,
  };
}
